import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useQuery } from '@tanstack/react-query';
import { getMonthRange } from '../pages/vacation/utils';
import { panelDrProfile, panelGateway, panelLegacyV1 } from './client';

export interface PanelCenter {
  id: number | string;
  name: string;
  address: string;
  type_id?: number;
  user_center_id?: number | string;
  [key: string]: unknown;
}

export interface Vacation {
  id?: string;
  from: number;
  to: number;
  centerId?: number;
}

export interface VacationResponse {
  status: string;
  message?: string;
  data?: { books_count?: number };
}

export interface BookConflictResponse {
  status: 'BOOK_CONFLICT';
  message: string;
  data: { books_count: number };
}

export interface PanelHoliday {
  date: string | number;
  title: string;
}

/** drprofile /api/users/{id}/centers — same call and filter as the providers panel (type 1 offices + online visit 5532). */
export const getUserCenters = async (userId: number | string): Promise<PanelCenter[]> => {
  const { data } = await panelDrProfile.get(`/api/users/${userId}/centers`, {
    withCredentials: false,
    params: { t: Date.now() },
  });
  const rawList = Array.isArray(data) ? data : data?.data || data?.centers || [];
  const list: any[] = Array.isArray(rawList)
    ? rawList.filter((c: any) => {
        const tid = c.type_id ?? c.typeId;
        const id = String(c.id);
        return (id === '5532' && tid === 3) || tid === 1;
      })
    : [];
  return list.map((c: any) => ({
    ...c,
    name: c.name ?? c.title ?? '',
    address: c.address ?? '',
    type_id: c.type_id ?? c.typeId,
  }));
};

const centersCacheKey = (userId: unknown) => `p24-panel-centers:${userId}`;

/** Last centers answer on this phone, so the center row is there before the request returns. */
const readCachedCenters = (userId: unknown): PanelCenter[] | undefined => {
  if (typeof window === 'undefined' || !userId) return undefined;
  try {
    const parsed = JSON.parse(localStorage.getItem(centersCacheKey(userId)) ?? 'null');
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export const useDoctorPanelCenters = () => {
  const userId = useUserInfoStore(state => state.info?.id);
  return useQuery(
    ['doctorPanel', 'centers', userId],
    async () => {
      const centers = await getUserCenters(userId as string);
      try {
        localStorage.setItem(centersCacheKey(userId), JSON.stringify(centers));
      } catch {
        // storage blocked: no warm start next time
      }
      return centers;
    },
    {
      enabled: !!userId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      initialData: () => readCachedCenters(userId),
      initialDataUpdatedAt: 0,
    },
  );
};

export const getHolidays = async (): Promise<PanelHoliday[]> => {
  const { data } = await panelGateway.get('/v1/luthuli/holidays-next-year');
  const raw = data as PanelHoliday[] | { data?: PanelHoliday[]; holidays?: PanelHoliday[] } | null | undefined;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw!.data!;
  if (Array.isArray(raw?.holidays)) return raw!.holidays!;
  return [];
};

export const getVacations = async (centerId: number | string, from: number, to: number): Promise<Vacation[]> => {
  const { data } = await panelLegacyV1.get(`/doctor/vacation/${centerId}`, { params: { from, to } });
  if (Array.isArray(data)) return data;
  if (data?.data && Array.isArray(data.data)) return data.data;
  if (data?.vacations && Array.isArray(data.vacations)) return data.vacations;
  return [];
};

export const createVacation = async (
  centerId: number | string,
  vacation: { from: number; to: number },
): Promise<VacationResponse> => {
  const { data } = await panelLegacyV1.post(`/doctor/vacation/${centerId}`, vacation);
  return data;
};

export const updateVacation = async (
  centerId: number | string,
  vacation: { from: number; to: number; old_from: number; old_to: number },
): Promise<VacationResponse> => {
  const { data } = await panelLegacyV1.put(`/doctor/vacation/${centerId}`, vacation);
  return data;
};

export const deleteVacation = async (centerId: number | string, from: number, to: number): Promise<VacationResponse> => {
  const { data } = await panelLegacyV1.delete(`/doctor/vacation/${centerId}`, { params: { from, to } });
  return data;
};

export const moveBooks = async (
  centerId: string,
  body: { book_from: number; book_to: number; target_from: number; confirmed: boolean },
): Promise<VacationResponse> => {
  const { data } = await panelLegacyV1.post(`/doctor/books/${centerId}/move`, body);
  return data;
};

export const deleteBooks = async (centerId: string, body: { from: number; to: number }): Promise<VacationResponse> => {
  const { data } = await panelLegacyV1.post(`/doctor/books/${centerId}/delete`, body);
  return data;
};

/** اطلاع‌رسانی مرخصی به مرکز (برای مراکز بدون دسترسی) */
export const notifyVacation = async (
  notifyCell: string,
  vacationData: { from: number; to: number; fullDay: boolean; fromTime: string; toTime: string },
  center: unknown,
): Promise<any> => {
  const { data } = await panelGateway.post('/v1/n8n-nelson/webhook/Notify', {
    notifyCell,
    from: vacationData.from,
    fromTime: vacationData.fullDay ? '' : vacationData.fromTime,
    fullDay: vacationData.fullDay,
    to: vacationData.to,
    toTime: vacationData.fullDay ? '' : vacationData.toTime,
    center,
  });
  return data;
};

export const useHolidays = () =>
  useQuery(['doctorPanel', 'holidays'], getHolidays, { staleTime: 6 * 60 * 60 * 1000, retry: 1, refetchOnWindowFocus: false });

export const vacationsKey = (centerId: number | string) => ['doctorPanel', 'vacations', String(centerId)];

export const useVacations = (centerId: number | string | undefined, year: number, month: number, enabled: boolean) =>
  useQuery(
    [...vacationsKey(centerId ?? ''), year, month],
    async () => {
      const { from, to } = getMonthRange(year, month);
      const data = await getVacations(centerId as number | string, from, to);
      return [...data].sort((a, b) => b.from - a.from);
    },
    { enabled: enabled && !!centerId, staleTime: 60 * 1000, keepPreviousData: true, refetchOnWindowFocus: false },
  );
